import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';

// Notifies the native shell of every React Router navigation, and
// listens for __rn_navigate events so the native back button can
// navigate within the SPA without triggering a full page reload.
function RouteTracker() {
  const location = useLocation();
  const navigate = useNavigate();

  // Tell the native shell which path we're on.
  useEffect(() => {
    if (window.NativeBridge && typeof window.NativeBridge.routeChange === 'function') {
      window.NativeBridge.routeChange(location.pathname);
    }
  }, [location.pathname]);

  // Listen for native-side navigation requests (e.g. hardware back → home).
  useEffect(() => {
    const handleNativeNav = (e) => {
      const path = e?.detail?.path;
      if (path) navigate(path, { replace: true });
    };
    window.addEventListener('__rn_navigate', handleNativeNav);
    return () => window.removeEventListener('__rn_navigate', handleNativeNav);
  }, [navigate]);

  return null;
}
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WhitelabelProvider, useWhitelabel } from './context/WhitelabelContext';
import colors from './common/Colors';

// Import screens
import LoginScreen from './screens/LoginScreen';
import ForgotPasswordScreen from './screens/ForgotPasswordScreen';
import OtpVerificationScreen from './screens/OtpVerificationScreen';
import ResetPasswordScreen from './screens/ResetPasswordScreen';
import Layout from './components/Layout';
import HomeScreen from './screens/HomeScreen';
import ProfileScreen from './screens/ProfileScreen';
import AttendanceScreen from './screens/AttendanceScreen';
import AssignmentScreen from './screens/AssignmentScreen';
import AssignmentSubmitScreen from './screens/AssignmentSubmitScreen';
import NoticeScreen from './screens/NoticeScreen';
import MessageScreen from './screens/MessageScreen';
import MessageDetailsScreen from './screens/MessageDetailsScreen';
import OnlineClassScreen from './screens/OnlineClassScreen';
import OnlineExamScreen from './screens/OnlineExamScreen';
import OnlineExamResultScreen from './screens/OnlineExamResultScreen';
import BillsScreen from './screens/BillsScreen';
import BirthdayScreen from './screens/BirthdayScreen';
import ReceiptsScreen from './screens/ReceiptsScreen';
import PhotoGalleryScreen from './screens/PhotoGalleryScreen';
import CircularScreen from './screens/CircularScreen';
import MyTeachersScreen from './screens/MyTeachersScreen';
import ChangePasswordScreen from './screens/ChangePasswordScreen';
import NotificationsScreen from './screens/NotificationsScreen';
import OnlinePaymentScreen from './screens/OnlinePaymentScreen';
import VideoScreen from './screens/VideoScreen';

// Dynamic theme creator based on whitelabel config
const createDynamicTheme = (whitelabelConfig) => {
  const primaryColor = whitelabelConfig?.primaryColor || colors.BLUE;
  const secondaryColor = whitelabelConfig?.secondaryColor || colors.ORANGE;

  return createTheme({
    palette: {
      primary: {
        main: primaryColor,
      },
      secondary: {
        main: secondaryColor,
      },
      success: {
        main: colors.GREEN,
      },
      error: {
        main: colors.RED,
      },
    },
    typography: {
      fontFamily: [
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
      ].join(','),
    },
    shape: {
      borderRadius: 12,
    },
    spacing: 8,
    components: {
      MuiButton: {
        defaultProps: { size: 'medium', disableElevation: true },
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 10,
            fontWeight: 600,
          },
        },
      },
      MuiTextField: {
        defaultProps: { size: 'small', variant: 'outlined' },
      },
      MuiCard: {
        styleOverrides: {
          root: { borderRadius: 12 },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: { paddingTop: 12, paddingBottom: 12 },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { boxShadow: '0 1px 3px rgba(0,0,0,0.12)' },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: { minHeight: 56 },
        },
      },
    },
  });
};

// Theme wrapper component that uses whitelabel
const ThemedApp = ({ children }) => {
  const { config } = useWhitelabel();
  const theme = createDynamicTheme(config);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { userToken, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  // Fall back to localStorage so navigation right after signIn() isn't
  // blocked by the async React state update not having landed yet.
  const token = userToken || localStorage.getItem('userToken');
  return token ? children : <Navigate to="/login" />;
};

// Public Route Component (redirects to home if logged in)
const PublicRoute = ({ children }) => {
  const { userToken, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return userToken ? <Navigate to="/home" /> : children;
};

// Clears auth state and redirects to login — used by native drawer logout
function LogoutRoute() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    signOut();
    window.NativeBridge?.clearSession();
    navigate('/login', { replace: true });
  }, []);
  return null;
}

function App() {
  return (
    <WhitelabelProvider>
      <ThemedApp>
        <AuthProvider>
        <Router>
          <RouteTracker />
          <Routes>
            {/* Public Routes */}
            <Route 
              path="/login" 
              element={
                <PublicRoute>
                  <LoginScreen />
                </PublicRoute>
              } 
            />
            <Route 
              path="/forgot-password" 
              element={
                <PublicRoute>
                  <ForgotPasswordScreen />
                </PublicRoute>
              } 
            />
            <Route 
              path="/verify-otp" 
              element={
                <PublicRoute>
                  <OtpVerificationScreen />
                </PublicRoute>
              } 
            />
            <Route 
              path="/reset-password" 
              element={
                <PublicRoute>
                  <ResetPasswordScreen />
                </PublicRoute>
              } 
            />

            {/* Protected Routes */}
            <Route 
              path="/home" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <HomeScreen />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <ProfileScreen />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/attendance" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <AttendanceScreen />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/assignments" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <AssignmentScreen />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/assignment-submit" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <AssignmentSubmitScreen />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/notices" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <NoticeScreen />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/messages" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <MessageScreen />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/message-details" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <MessageDetailsScreen />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/notifications" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <NotificationsScreen />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/online-classes" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <OnlineClassScreen />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/online-exams" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <OnlineExamScreen />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/exam-results" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <OnlineExamResultScreen />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/bills" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <BillsScreen />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/birthday" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <BirthdayScreen />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/receipts" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <ReceiptsScreen />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/online-payment" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <OnlinePaymentScreen />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/gallery" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <PhotoGalleryScreen />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/videos" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <VideoScreen />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/circulars" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <CircularScreen />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/teachers" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <MyTeachersScreen />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/change-password" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <ChangePasswordScreen />
                  </Layout>
                </ProtectedRoute>
              } 
            />

            {/* Logout route — clears session then goes to login */}
            <Route path="/logout" element={<LogoutRoute />} />

            {/* Default Route */}
            <Route path="/" element={<Navigate to="/home" />} />
            <Route path="*" element={<Navigate to="/home" />} />
          </Routes>
        </Router>
        </AuthProvider>
      </ThemedApp>
    </WhitelabelProvider>
  );
}

export default App;
